import { defineStore, storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import { useLayers } from './layers'
import { useStore } from 'vuex'
import { useReport } from '@/hooks/reports/useReport'
import { useRoute, useRouter } from 'vue-router'

const levelsOfDetail = ['LEVEL', 'SPACE'] as const
export type LevelOfDetail = (typeof levelsOfDetail)[number]

const levelsOfDetailToLayerMap: Record<LevelOfDetail, string> = {
  SPACE: 'spaces-3d',
  LEVEL: 'levels'
}

export const useLevelOfDetail = defineStore('levelOfDetail', () => {
  const { hasSpaces } = storeToRefs(useReport())
  const router = useRouter()
  const route = useRoute()
  const currentLevelOfDetail = ref<LevelOfDetail>(
    (route.query.lod as LevelOfDetail) || levelsOfDetail[0]
  )
  const layers = useLayers()
  const store = useStore()

  function setLevelOfDetail(value: LevelOfDetail) {
    const next = levelsOfDetailToLayerMap[value]
    const current = levelsOfDetailToLayerMap[currentLevelOfDetail.value]
    if (next) {
      layers.showLayer(next)
    }
    if (current && next !== current) {
      layers.hideLayer(current)
    }
    if (store?.state.selectedBuilding) {
      store.commit('RESET_CURRENT_ROOM')
      store.dispatch('RESET_CURRENT_LEVEL')
      store.commit('SET_CLICKED_ITEM_TYPE', { type: 'building' })
    }
    if (value !== 'LEVEL') {
      router.push({ query: { ...route.query, lod: value } })
    } else {
      router.push({ query: { ...route.query, lod: undefined } })
    }
    currentLevelOfDetail.value = value
  }

  function cycleLevelOfDetail() {
    const currentIndex = levelsOfDetail.indexOf(currentLevelOfDetail.value)
    const nextIndex = (currentIndex + 1) % levelsOfDetail.length
    setLevelOfDetail(levelsOfDetail[nextIndex])
  }

  function hideAllLayers() {
    for (const layer of Object.values(levelsOfDetailToLayerMap)) {
      layers.hideLayer(layer)
    }
  }

  watch(
    [hasSpaces, currentLevelOfDetail],
    () => {
      if (currentLevelOfDetail.value !== 'LEVEL' && hasSpaces.value) {
        layers.hideLayer('levels')
      } else {
        layers.showLayer('levels')
      }
    },
    { immediate: true }
  )

  hideAllLayers()
  setLevelOfDetail(currentLevelOfDetail.value)

  return {
    setLevelOfDetail,
    cycleLevelOfDetail,
    currentLevelOfDetail
  }
})
